/**
 * dashboardHelpers — the pure view logic behind the post list: why it is empty,
 * what the Ausstehend counts actually mean, and when the list needs refreshing.
 *
 * All of it is deliberately free of React so it can be tested directly, the way
 * fullscreenHelpers / reviewDesignHelpers are.
 *
 * NO ES2020 logical-assignment (`||=`/`&&=`/`??=`) — CI-guarded by
 * sourceLogicalAssignment.test.js.
 *
 * @module AdminSocialPostsPage/dashboardHelpers
 */

import { isReviewerQueuePost } from './reviewerQueuePost';

// ---- Empty state (F8) ----

/**
 * Why the list is empty — and what to do about it. "Keine Beiträge gefunden."
 * looked identical whether the automation was switched off, every workflow was
 * paused, or simply nothing had triggered yet; only the last of those is normal.
 *
 * `settings` may be null (not fetched yet): an unknown switch is never reported as
 * "off", the neutral message stands in until the truth arrives.
 *
 * @param {{settings:?Object, workflows:?Array, workflowFilter:?string}} input
 * @returns {{message:string, hint:?string, action:?{label:string, tab:string}}}
 */
export const dashboardEmptyState = ({ settings, workflows, workflowFilter } = {}) => {
    if (settings && settings.enabled === false) {
        return {
            message: 'Die Automatisierung ist ausgeschaltet.',
            hint: 'Solange sie aus ist, werden keine Beiträge erzeugt.',
            action: { label: 'Zu den Einstellungen', tab: 'settings' },
        };
    }

    const list = workflows || [];
    if (list.length === 0) {
        return {
            message: 'Es gibt noch keinen Workflow.',
            hint: 'Ein Workflow legt fest, welches Ereignis welche Beiträge erzeugt.',
            action: { label: 'Workflow anlegen', tab: 'workflow' },
        };
    }
    if (!list.some(w => w && w.enabled)) {
        return {
            message: 'Alle Workflows sind pausiert.',
            hint: 'Pausierte Workflows reagieren auf kein Ereignis.',
            action: { label: 'Zu den Workflows', tab: 'workflow' },
        };
    }

    // Nothing is wrong — the filter is simply narrow, or nothing has fired yet.
    if (workflowFilter) {
        return {
            message: 'Keine Beiträge für diesen Workflow gefunden.',
            hint: null,
            action: null,
        };
    }
    return {
        message: 'Keine Beiträge gefunden.',
        hint: 'Die Trigger starten von selbst, sobald ein passendes Ereignis eintritt.',
        action: null,
    };
};

// ---- Ausstehend counts (F5) ----

/**
 * ONE counting rule for the Ausstehend tab, because the badge and the list used to
 * measure different things: the badge counted the REVIEWER QUEUE (posts you can
 * actually approve) while the tab also lists posts that are still being generated
 * or are waiting on manual Community work. Both numbers are true and neither is
 * the other, so the badge now says "zu prüfen" (queue) and this caption names the
 * remainder — derived from the very list on screen, so it can never contradict it.
 *
 * @param {Array} posts the loaded Ausstehend list
 * @returns {{total:number, toReview:number, inProgress:number}}
 */
export const pendingTabCounts = posts => {
    const list = posts || [];
    const toReview = list.filter(isReviewerQueuePost).length;
    return { total: list.length, toReview, inProgress: list.length - toReview };
};

/**
 * The caption under the Ausstehend tab, or null when there is nothing to split.
 * @param {{total:number, toReview:number, inProgress:number}} counts
 * @returns {string|null}
 */
export const pendingTabCaption = counts => {
    const { total, toReview, inProgress } = counts || {};
    if (!total) return null;
    if (!inProgress) return `${toReview} zu prüfen`;
    if (!toReview) return `${inProgress} in Arbeit`;
    return `${toReview} zu prüfen · ${inProgress} in Arbeit`;
};

// ---- Auto-refresh (F4) ----

// A post in one of these statuses is being worked on by the server right now, so
// its row WILL change without anyone touching the page.
const IN_FLIGHT_STATUSES = ['queued', 'generating', 'publishing'];

/**
 * Does the list hold a row the server is still moving? Polling exists for exactly
 * this case and must stop the moment it passes — a settled list refreshes nothing.
 * @param {Array} posts
 * @returns {boolean}
 */
export const hasInFlightPost = posts =>
    (posts || []).some(p => p && IN_FLIGHT_STATUSES.includes(p.status));

/**
 * How fresh the list is, in words. Seconds only matter while they are few.
 * @param {number} elapsedMs since the last successful load
 * @returns {string}
 */
export const refreshAgoLabel = elapsedMs => {
    const ms = Number.isFinite(elapsedMs) && elapsedMs > 0 ? elapsedMs : 0;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 5) return 'Gerade aktualisiert';
    if (seconds < 60) return `Aktualisiert vor ${seconds} s`;
    const minutes = Math.floor(seconds / 60);
    return minutes === 1 ? 'Aktualisiert vor 1 Minute' : `Aktualisiert vor ${minutes} Minuten`;
};

/** How often the list re-queries while something is in flight. */
export const REFRESH_POLL_MS = 25000;

// ---- Unpublished workflow drafts ----
//
// A workflow row carries `draftGraph` when the studio autosaved edits that were
// never published. The workflow KEEPS RUNNING its published graph — so a draft is
// a state the operator must see, or they believe edits are live that are not.
// (Measured on prod 2026-07-29: `lcee` and `fvkd` both sat in exactly this state.)
// The copy is deliberately calm: this is not an error, it is an unfinished step.

/** The one sentence that explains what an unpublished draft actually means. */
export const DRAFT_CONSEQUENCE =
    'Bis zur Veröffentlichung läuft weiterhin die zuletzt veröffentlichte Version.';

/**
 * The unpublished-draft notice for ONE workflow row, or null when it has none.
 * `label` is the chip, `text` the consequence, `actionLabel` the way out.
 *
 * @param {?Object} workflow a workflow row (needs `draftGraph`)
 * @returns {?{label:string, text:string, actionLabel:string}}
 */
export const unpublishedDraftNotice = workflow => {
    if (!workflow || !workflow.draftGraph) return null;
    return {
        label: 'Nicht veröffentlicht',
        text: `Dieser Workflow hat gespeicherte Änderungen, die noch nicht veröffentlicht sind. ${DRAFT_CONSEQUENCE}`,
        actionLabel: 'Entwurf öffnen',
    };
};

/**
 * The list-level summary — the "unmissable" half: how many workflows sit on an
 * unpublished draft, and which ones. Null when every workflow is published.
 *
 * @param {?Array} workflows
 * @returns {?{count:number, names:string[], message:string, hint:string}}
 */
export const unpublishedDraftSummary = workflows => {
    const drafts = (workflows || []).filter(w => w && w.draftGraph);
    if (drafts.length === 0) return null;
    const names = drafts.map(w => w.name || 'Standard');
    const message =
        drafts.length === 1
            ? `„${names[0]}“ hat gespeicherte Änderungen, die noch nicht veröffentlicht sind.`
            : `${
                  drafts.length
              } Workflows haben gespeicherte Änderungen, die noch nicht veröffentlicht sind: ${names
                  .map(n => `„${n}“`)
                  .join(', ')}.`;
    return { count: drafts.length, names, message, hint: DRAFT_CONSEQUENCE };
};

// ---- Blocked workflow delete (409) ----

// The server refuses to delete a workflow while posts it minted are still in
// flight, and says so in German with a count (WorkflowRepo.destroy). The count is
// the only actionable part, so we read it back out and pair it with the ONE next
// step: open that workflow's posts and settle them.
const IN_FLIGHT_COUNT_RE = /noch\s+(\d+)\s+Beitr/i;

/**
 * Turn a failed workflow delete into a next step. A blocked delete (posts still in
 * flight) becomes a counted sentence plus a link to exactly those posts; anything
 * else stays the server's own message with no misleading action.
 *
 * @param {?string} message the server error message
 * @param {?string} slug the workflow whose delete failed
 * @returns {?{count:?number, text:string, action:?{label:string, href:string}}}
 */
export const workflowDeleteBlockedNotice = (message, slug) => {
    if (!message) return null;
    const match = IN_FLIGHT_COUNT_RE.exec(message);
    if (!match || !slug) {
        return { count: null, text: message, action: null };
    }
    const count = Number(match[1]);
    const posts = count === 1 ? 'ist noch 1 Beitrag' : `sind noch ${count} Beiträge`;
    return {
        count,
        text: `Der Workflow kann noch nicht gelöscht werden: ${posts} in Arbeit. Gib sie frei oder lösche sie — danach lässt sich der Workflow löschen.`,
        action: {
            label: 'Beiträge dieses Workflows öffnen',
            href: `/admin-social?tab=pending&workflow=${encodeURIComponent(slug)}`,
        },
    };
};
